import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "periodType",           // NEW (replaces councilType)
    "councilType",          // Keep for backward compatibility
    "academicYear", 
    "pedagogicMonth",       // NEW
    "pedagogicMonthSection", // NEW
    "trimester", 
    "trimesterSection", 
    "councilInfo",          // Keep for backward compatibility
    "periodTypeError",      // NEW
    "councilTypeError",     // Keep for backward compatibility
    "academicYearError", 
    "trimesterError",
    "pedagogicMonthError"   // NEW
  ]
  static values = { translations: Object }

  connect() {
    console.log("Council minutes step2 controller connected")
    
    // Automatically load trimesters/pedagogic months on page load if period type and academic year are set
    if (this.hasPeriodTypeTarget && this.hasAcademicYearTarget) {
      const periodType = this.periodTypeTarget.value
      const academicYearId = this.academicYearTarget.value
      
      if (periodType && academicYearId) {
        if (periodType === 'pedagogical_month') {
          this.loadPedagogicMonths(academicYearId)
        } else if (periodType === 'trimester') {
          this.loadTrimesters(academicYearId)
        }
      }
    }
  }

  updatePeriodType(event) {
    const periodType = event.target.value
    console.log("Period type changed to:", periodType)
    
    // Hide both sections initially
    if (this.hasPedagogicMonthSectionTarget) {
      this.pedagogicMonthSectionTarget.classList.add('hidden')
    }
    if (this.hasTrimesterSectionTarget) {
      this.trimesterSectionTarget.classList.add('hidden')
    }
    
    // Academic year is now always set (hidden field with active year)
    // So we can always load the appropriate data when period type changes
    const academicYearId = this.hasAcademicYearTarget ? this.academicYearTarget.value : null
    
    // Show appropriate section based on selection
    if (periodType === 'pedagogical_month') {
      if (this.hasPedagogicMonthSectionTarget) {
        this.pedagogicMonthSectionTarget.classList.remove('hidden')
      }
      // Load pedagogic months (academic year is always set to active year)
      if (academicYearId) {
        this.loadPedagogicMonths(academicYearId)
      }
    } else if (periodType === 'trimester') {
      if (this.hasTrimesterSectionTarget) {
        this.trimesterSectionTarget.classList.remove('hidden')
      }
      // Load trimesters (academic year is always set to active year)
      if (academicYearId) {
        this.loadTrimesters(academicYearId)
      }
    }
    // academic_year: both sections remain hidden
    
    // Clear error if value is selected
    if (periodType && this.hasPeriodTypeErrorTarget) {
      this.hideError(this.periodTypeErrorTarget)
    }
  }

  // Keep updateCouncilType for backward compatibility
  updateCouncilType(event) {
    const councilType = event.target.value
    console.log("Council type changed to:", councilType)
    
    // Afficher/masquer la section trimestre selon le type de conseil
    if (councilType === 'annual_summary') {
      if (this.hasTrimesterSectionTarget) {
        this.trimesterSectionTarget.classList.add('hidden')
      }
      // Masquer également une éventuelle erreur de trimestre
      if (this.hasTrimesterErrorTarget) this.hideError(this.trimesterErrorTarget)
    } else {
      if (this.hasTrimesterSectionTarget) {
        this.trimesterSectionTarget.classList.remove('hidden')
      }
    }
    
    // Effacer une erreur éventuelle si une valeur est choisie
    if (councilType && this.hasCouncilTypeErrorTarget) this.hideError(this.councilTypeErrorTarget)

    // Mettre à jour les informations contextuelles
    this.updateCouncilInfo(councilType)
  }

  // updateAcademicYear is no longer needed since academic year is now a hidden field
  // Always set to active year, so this method is kept for backward compatibility but does nothing
  updateAcademicYear(event) {
    // Academic year is now always set to active year (hidden field)
    // This method is kept for backward compatibility but no longer needed
    console.log("Academic year is now fixed to active year, no action needed")
  }

  loadTrimesters(academicYearId) {
    const url = `/schools/docs/council_minutes/get_trimesters?academic_year_id=${academicYearId}`
    
    console.log("Loading trimesters from:", url)
    
    fetch(url, {
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      console.log('Turbo response received for trimesters')
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error loading trimesters:', error)
    })
  }

  loadPedagogicMonths(academicYearId) {
    const url = `/schools/docs/council_minutes/get_pedagogic_months?academic_year_id=${academicYearId}`
    
    console.log("Loading pedagogic months from:", url)
    
    fetch(url, {
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.text()
    })
    .then(html => {
      console.log('Turbo response received for pedagogic months')
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error loading pedagogic months:', error)
    })
  }

  clearTrimesters() {
    // Vider le select des trimestres
    if (this.hasTrimesterTarget) {
      // Get the translated prompt from translations or use fallback
      const translations = this.translationsValue
      const prompt = translations?.trimester_prompt || 'Select the trimester'
      this.trimesterTarget.innerHTML = `<option value="">${prompt}</option>`
    }
  }

  clearPedagogicMonths() {
    // Vider le select des mois pédagogiques
    if (this.hasPedagogicMonthTarget) {
      const prompt = 'Select the evaluation period'
      this.pedagogicMonthTarget.innerHTML = `<option value="">${prompt}</option>`
    }
  }

  updatePedagogicMonth(event) {
    const value = event.target.value
    if (value && this.hasPedagogicMonthErrorTarget) {
      this.hideError(this.pedagogicMonthErrorTarget)
    }
  }

  updateCouncilInfo(councilType) {
    // Mettre à jour les informations contextuelles selon le type de conseil
    const titleElement = document.getElementById('council-type-title')
    const descriptionElement = document.getElementById('council-type-description')
    
    if (titleElement && descriptionElement) {
      const info = this.getCouncilTypeInfo(councilType)
      titleElement.textContent = info.title
      descriptionElement.textContent = info.description
    }
  }

  getCouncilTypeInfo(councilType) {
    // Get translations from the data attribute
    const translations = this.translationsValue
    
    if (translations && translations.titles && translations.descriptions) {
      const academicYear = this.hasAcademicYearTarget ? 
        this.academicYearTarget.options[this.academicYearTarget.selectedIndex]?.text : ''
      
      let description = translations.descriptions[councilType] || translations.fallback.description
      if (description && academicYear) {
        description = description.replace('%{academic_year}', academicYear)
      }
      
      return {
        title: translations.titles[councilType] || translations.fallback.title,
        description: description
      }
    }
    
    // Fallback if no translations available
    return {
      title: 'Council type',
      description: 'Select a council type for more information'
    }
  }

  updateTrimester(event) {
    const value = event.target.value
    if (value && this.hasTrimesterErrorTarget) this.hideError(this.trimesterErrorTarget)
  }

  showError(el, message) {
    if (!el) return
    el.textContent = message
    el.classList.remove('hidden')
    el.classList.add('animate-pulse')
    setTimeout(() => el.classList.remove('animate-pulse'), 600)
  }

  hideError(el) {
    if (!el) return
    el.textContent = ''
    el.classList.add('hidden')
  }
}